/**
 * Filter Adapter
 *
 * Converts between the recursive FilterGroup (API format) and entity-specific
 * flat filter objects (PropertyFilters, TenantFilters, ContactFilters, WeatherEventFilters).
 *
 * Each entity type registers a field mapping config. The adapter uses these
 * configs to bidirectionally convert between formats.
 */

import type {
    FilterGroup,
    FilterCondition,
    FilterOperator,
    SavedViewEntityType,
} from '../api/types'
import { isFilterCondition, isFilterGroup, formatTokenValue } from '../api/types'

// ─── Field Mapping Config ───────────────────────────────────────────

/** How an entity field maps to a filter condition */
export interface FieldMapping {
    /** Default operator for this field */
    op: FilterOperator
    /** Field value type — determines how values are parsed */
    type: 'string' | 'number' | 'boolean' | 'enum' | 'date' | 'relation' | 'string[]'
    /** Flat filter key (if different from API field name) */
    flatKey?: string
}

/** Adapter config for an entity type */
export interface EntityAdapterConfig {
    entityType: SavedViewEntityType
    fields: Record<string, FieldMapping>
}

// ─── Entity Configs ─────────────────────────────────────────────────

const propertyConfig: EntityAdapterConfig = {
    entityType: 'property',
    fields: {
        state:                   { op: '$eq', type: 'string' },
        city:                    { op: '$eq', type: 'string' },
        industryClassification:  { op: '$eq', type: 'enum' },
        buildingType:            { op: '$eq', type: 'enum' },
        addressVerdict:          { op: '$eq', type: 'enum' },
        search:                  { op: '$contains', type: 'string' },
        productId:               { op: '$eq', type: 'relation' },
    },
}

const tenantConfig: EntityAdapterConfig = {
    entityType: 'tenant',
    fields: {
        propertyId:    { op: '$eq', type: 'relation' },
        industry:      { op: '$eq', type: 'string' },
        matchType:     { op: '$eq', type: 'enum' },
        hasDomain:     { op: '$exists', type: 'boolean' },
        minConfidence: { op: '$gte', type: 'number' },
        domain:        { op: '$eq', type: 'string' },
        city:          { op: '$eq', type: 'string' },
        state:         { op: '$eq', type: 'string' },
        search:        { op: '$contains', type: 'string' },
    },
}

const contactConfig: EntityAdapterConfig = {
    entityType: 'contact',
    fields: {
        source:    { op: '$eq', type: 'string' },
        seniority: { op: '$eq', type: 'string' },
        role:      { op: '$eq', type: 'string' },
        email:     { op: '$eq', type: 'string' },
        title:     { op: '$contains', type: 'string' },
        search:    { op: '$contains', type: 'string' },
    },
}

const leadConfig: EntityAdapterConfig = {
    entityType: 'lead',
    fields: {
        status:              { op: '$eq', type: 'enum' },
        priority:            { op: '$eq', type: 'enum' },
        source:              { op: '$eq', type: 'enum' },
        score:               { op: '$gte', type: 'number' },
        minScore:            { op: '$gte', type: 'number', flatKey: 'minScore' },
        maxScore:            { op: '$lte', type: 'number', flatKey: 'maxScore' },
        propertyId:          { op: '$eq', type: 'relation' },
        companyName:         { op: '$contains', type: 'string' },
        title:               { op: '$contains', type: 'string' },
        primaryContactName:  { op: '$contains', type: 'string' },
        primaryContactEmail: { op: '$contains', type: 'string' },
        search:              { op: '$contains', type: 'string' },
    },
}

const weatherEventConfig: EntityAdapterConfig = {
    entityType: 'weather_event',
    fields: {
        eventType:    { op: '$eq', type: 'enum' },
        confidence:   { op: '$eq', type: 'enum' },
        from:         { op: '$gte', type: 'date', flatKey: 'from' },
        to:           { op: '$lte', type: 'date', flatKey: 'to' },
        minSize:      { op: '$gte', type: 'number' },
        minWindSpeed: { op: '$gte', type: 'number' },
        maxDistance:   { op: '$lte', type: 'number' },
        states:       { op: '$in', type: 'string[]' },
        cities:       { op: '$in', type: 'string[]' },
        industries:   { op: '$in', type: 'string[]' },
        buildingTypes: { op: '$in', type: 'string[]' },
        productIds:   { op: '$in', type: 'string[]' },
        search:       { op: '$contains', type: 'string' },
    },
}

const projectConfig: EntityAdapterConfig = {
    entityType: 'project',
    fields: {
        propertyId:      { op: '$eq', type: 'relation' },
        type:            { op: '$eq', type: 'enum' },
        status:          { op: '$eq', type: 'enum' },
        externalSystem:  { op: '$eq', type: 'string' },
        search:          { op: '$contains', type: 'string' },
    },
}

const weatherReportConfig: EntityAdapterConfig = {
    entityType: 'weather_report',
    fields: {
        title:    { op: '$contains', type: 'string' },
        isActive: { op: '$eq', type: 'boolean' },
        search:   { op: '$contains', type: 'string' },
    },
}

const weatherReportLeadConfig: EntityAdapterConfig = {
    entityType: 'weather_report_lead',
    fields: {
        search:     { op: '$contains', type: 'string' },
        reportId:   { op: '$eq', type: 'relation' },
        propertyId: { op: '$eq', type: 'relation' },
    },
}

const fleetMachineConfig: EntityAdapterConfig = {
    entityType: 'fleet_machine',
    fields: {
        hostname: { op: '$contains', type: 'string' },
        status:   { op: '$eq', type: 'enum' },
        username: { op: '$contains', type: 'string' },
        osVersion: { op: '$contains', type: 'string' },
        search:   { op: '$contains', type: 'string' },
    },
}

const fleetAppClassificationConfig: EntityAdapterConfig = {
    entityType: 'fleet_app_classification',
    fields: {
        processName:    { op: '$contains', type: 'string' },
        classification: { op: '$eq', type: 'enum' },
        search:         { op: '$contains', type: 'string' },
    },
}

const fleetDomainClassificationConfig: EntityAdapterConfig = {
    entityType: 'fleet_domain_classification',
    fields: {
        domain:         { op: '$contains', type: 'string' },
        classification: { op: '$eq', type: 'enum' },
        search:         { op: '$contains', type: 'string' },
    },
}

const fleetDailySummaryConfig: EntityAdapterConfig = {
    entityType: 'fleet_daily_summary',
    fields: {
        username: { op: '$contains', type: 'string' },
        date:     { op: '$gte', type: 'date' },
        search:   { op: '$contains', type: 'string' },
    },
}

const contractReviewConfig: EntityAdapterConfig = {
    entityType: 'contract_review',
    fields: {
        status:               { op: '$eq', type: 'enum' },
        overallRiskRating:    { op: '$eq', type: 'enum' },
        originalFileName:     { op: '$contains', type: 'string' },
        criticalIssueCount:   { op: '$gte', type: 'number' },
        highIssueCount:       { op: '$gte', type: 'number' },
        mediumIssueCount:     { op: '$gte', type: 'number' },
        estimatedCost:        { op: '$gte', type: 'number' },
        reportUrl:            { op: '$exists', type: 'boolean' },
        createdAt:            { op: '$gte', type: 'date' },
        completedAt:          { op: '$gte', type: 'date' },
        search:               { op: '$contains', type: 'string' },
    },
}

const optimizerJobConfig: EntityAdapterConfig = {
    entityType: 'optimizer_job',
    fields: {
        status:        { op: '$eq', type: 'enum' },
        name:          { op: '$contains', type: 'string' },
        description:   { op: '$contains', type: 'string' },
        createdByUser: { op: '$in', type: 'relation' },
        projectId:     { op: '$eq', type: 'string' },
        createdAt:     { op: '$gte', type: 'date' },
        updatedAt:     { op: '$gte', type: 'date' },
        search:        { op: '$contains', type: 'string' },
    },
}

const optimizerMaterialPresetConfig: EntityAdapterConfig = {
    entityType: 'optimizer_material_preset',
    fields: {
        name:                { op: '$contains', type: 'string' },
        materialType:        { op: '$eq', type: 'string' },
        vendor:              { op: '$eq', type: 'string' },
        finish:              { op: '$contains', type: 'string' },
        thickness:           { op: '$eq', type: 'string' },
        isDirectional:       { op: '$eq', type: 'boolean' },
        kerfThickness:       { op: '$eq', type: 'number' },
        returnLeg:           { op: '$eq', type: 'number' },
        fillerStripWidth:    { op: '$eq', type: 'number' },
        perimeterMargin:     { op: '$eq', type: 'number' },
        customLengthMinSqft: { op: '$gte', type: 'number' },
        minSheetsPerLength:  { op: '$gte', type: 'number' },
        createdAt:           { op: '$gte', type: 'date' },
        updatedAt:           { op: '$gte', type: 'date' },
        search:              { op: '$contains', type: 'string' },
    },
}

const itRequestConfig: EntityAdapterConfig = {
    entityType: 'it_request',
    fields: {
        status:     { op: '$eq', type: 'enum' },
        priority:   { op: '$eq', type: 'enum' },
        effort:     { op: '$eq', type: 'enum' },
        category:   { op: '$eq', type: 'string' },
        assignee:   { op: '$in', type: 'relation', flatKey: 'assigneeId' },
        assigneeId: { op: '$in', type: 'relation' },
        submitter:  { op: '$in', type: 'relation', flatKey: 'submitterId' },
        submitterId:{ op: '$in', type: 'relation' },
        teamId:     { op: '$eq', type: 'string' },
        dueDate:    { op: '$gte', type: 'date' },
        search:     { op: '$contains', type: 'string' },
    },
}

const fabJobConfig: EntityAdapterConfig = {
    entityType: 'fab_job',
    fields: {
        location:           { op: '$eq', type: 'enum' },
        status:             { op: '$eq', type: 'enum' },
        projectManagerUser: { op: '$in', type: 'relation' },
        jobName:            { op: '$contains', type: 'string' },
        jobNumber:          { op: '$contains', type: 'string' },
        release:            { op: '$contains', type: 'string' },
        panels:             { op: '$contains', type: 'string' },
        notes:              { op: '$contains', type: 'string' },
        otApproved:         { op: '$eq', type: 'boolean' },
        isAsapOnsite:       { op: '$eq', type: 'boolean' },
        position:           { op: '$gte', type: 'number' },
        onsiteDate:         { op: '$gte', type: 'date' },
        fabStartDate:       { op: '$gte', type: 'date' },
        fabEndDate:         { op: '$gte', type: 'date' },
        shipDate:           { op: '$gte', type: 'date' },
        createdAt:          { op: '$gte', type: 'date' },
        updatedAt:          { op: '$gte', type: 'date' },
        search:             { op: '$contains', type: 'string' },
    },
}

/** Registry of all entity adapter configs */
const adapterConfigs: Record<SavedViewEntityType, EntityAdapterConfig> = {
    property: propertyConfig,
    tenant: tenantConfig,
    contact: contactConfig,
    lead: leadConfig,
    weather_event: weatherEventConfig,
    project: projectConfig,
    weather_report: weatherReportConfig,
    weather_report_lead: weatherReportLeadConfig,
    fleet_machine: fleetMachineConfig,
    fleet_app_classification: fleetAppClassificationConfig,
    fleet_domain_classification: fleetDomainClassificationConfig,
    fleet_daily_summary: fleetDailySummaryConfig,
    contract_review: contractReviewConfig,
    optimizer_job: optimizerJobConfig,
    optimizer_material_preset: optimizerMaterialPresetConfig,
    it_request: itRequestConfig,
    fab_job: fabJobConfig,
}

/**
 * Get the adapter config for an entity type.
 * @internal — used only by flatToFilterGroup
 */
function getAdapterConfig(entityType: SavedViewEntityType): EntityAdapterConfig {
    return adapterConfigs[entityType]
}

// ─── Flat → FilterGroup ─────────────────────────────────────────────

/**
 * Convert a flat filter object (e.g., PropertyFilters) to a FilterGroup.
 *
 * Skips empty/null/undefined values and pagination params.
 * Returns an AND group of conditions.
 *
 * Used when converting legacy filter panel state into a FilterGroup
 * for saving views. The reverse conversion (FilterGroup → query) is
 * now handled server-side by the preview API.
 */
export function flatToFilterGroup(
    entityType: SavedViewEntityType,
    flatFilters: Record<string, unknown>,
): FilterGroup {
    const config = getAdapterConfig(entityType)
    const conditions: FilterCondition[] = []

    for (const [key, value] of Object.entries(flatFilters)) {
        // Skip empty values
        if (value === undefined || value === null || value === '') continue

        // Skip pagination/sort params — these aren't filters
        if (['page', 'limit', 'sortBy', 'sortOrder'].includes(key)) continue

        const mapping = config.fields[key]
        if (!mapping) continue // Unknown field — skip gracefully

        const fieldName = mapping.flatKey || key

        if (mapping.type === 'string[]' && Array.isArray(value)) {
            if (value.length > 0) {
                conditions.push({ field: fieldName, op: '$in', value })
            }
        } else if (mapping.type === 'boolean') {
            conditions.push({ field: fieldName, op: '$exists', value: Boolean(value) })
        } else if (mapping.type === 'number') {
            conditions.push({ field: fieldName, op: mapping.op, value: Number(value) })
        } else {
            conditions.push({ field: fieldName, op: mapping.op, value })
        }
    }

    return { operator: 'AND', conditions }
}

// ─── Utility ────────────────────────────────────────────────────────

/**
 * Check if a FilterGroup has any nested groups (non-flat).
 * If true, the group cannot be fully represented as flat filters.
 */
export function hasNestedGroups(group: FilterGroup): boolean {
    return group.conditions.some((child) => isFilterGroup(child))
}

/**
 * Get a human-readable summary of a FilterGroup for display in pills.
 */
export function getFilterSummary(group: FilterGroup): string[] {
    const summaries: string[] = []

    for (const child of group.conditions) {
        if (isFilterCondition(child)) {
            const isTokenVal = typeof child.value === 'string' && child.value.startsWith('$')
            let opLabel = getOperatorLabel(child.op)

            if (isTokenVal) {
                if (child.op === '$eq') opLabel = 'is'
                if (child.op === '$ne') opLabel = 'is not'
                if (child.op === '$lt') opLabel = 'is before'
                if (child.op === '$lte') opLabel = 'is on or before'
                if (child.op === '$gt') opLabel = 'is after'
                if (child.op === '$gte') opLabel = 'is on or after'
            }

            let valueStr: string
            if (child.op === '$in' && Array.isArray(child.value)) {
                const hasTokens = (child.value as unknown[]).some(v => typeof v === 'string' && v.startsWith('$'))
                if (hasTokens) {
                    valueStr = (child.value as string[]).map(v => formatTokenValue(v)).join(', ')
                } else {
                    valueStr = `(${(child.value as string[]).length})`
                }
            } else if (child.op === '$exists') {
                summaries.push(child.value ? `${child.field} exists` : `${child.field} does not exist`)
                continue
            } else if (Array.isArray(child.value)) {
                valueStr = (child.value as string[]).map(v => formatTokenValue(v)).join(', ')
            } else {
                valueStr = formatTokenValue(child.value)
            }

            summaries.push(`${child.field} ${opLabel} ${valueStr}`)
        } else if (isFilterGroup(child)) {
            const nested = getFilterSummary(child)
            if (nested.length > 0) {
                summaries.push(`(${nested.join(` ${child.operator} `)})`)
            }
        }
    }

    return summaries
}

/** Human-readable labels for filter operators */
function getOperatorLabel(op: FilterOperator): string {
    const labels: Record<FilterOperator, string> = {
        $eq: '=',
        $ne: '≠',
        $gt: '>',
        $gte: '≥',
        $lt: '<',
        $lte: '≤',
        $in: 'is any of',
        $nin: 'not in',
        $contains: 'contains',
        $regex: 'contains',
        $startsWith: 'starts with',
        $endsWith: 'ends with',
        $exists: 'exists',
    }
    return labels[op] || op
}
